import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { getProfile, putProfile } from '../../lib/jobsApi';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../lib/api';

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">Couldn&apos;t load your profile.</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const JobSeekerProfile: React.FC = () => {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    phone: '',
    location: '',
    bio: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [busy, setBusy] = useState(false);

  // Load profile on mount (and on retry, via loadNonce) — cancelled-guarded so
  // a fast unmount (or a stale in-flight retry) can't setState afterward,
  // same pattern as MyJobs.tsx. A failed load must NOT fall through to an
  // empty editable form — that would let a Save silently overwrite the
  // stored profile with blanks — so it renders an error block instead.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const loadProfile = async () => {
      try {
        const res = await getProfile();
        if (!cancelled && res.profile) {
          setFormData({
            title: res.profile.title || '',
            phone: res.profile.phone || '',
            location: res.profile.location || '',
            bio: res.profile.bio || '',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void loadProfile();
    return () => {
      cancelled = true;
    };
  }, [loadNonce]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;

    setBusy(true);
    try {
      await putProfile(formData);
      toast.success('Profile saved.');
    } catch (err) {
      if (err instanceof ApiError && err.fields) {
        setErrors(err.fields);
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Failed to save profile.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto" role="status">
        <span className="label-editorial block mb-3">Profile</span>
        <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">
          Loading…
        </h1>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto">
        <span className="label-editorial block mb-3">Profile</span>
        <ErrorBlock message={loadError} onRetry={() => setLoadNonce((n) => n + 1)} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <span className="label-editorial block mb-3">Profile</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">
        Your profile
      </h1>

      {/* Account block — read-only */}
      <div className="bg-paper border border-hairline rounded-md p-6 mb-8">
        <h2 className="label-editorial block mb-4">
          Account
        </h2>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-ink-tertiary uppercase tracking-widest mb-1">
              Name
            </p>
            <p className="text-ink">{user?.name}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-ink-tertiary uppercase tracking-widest mb-1">
              Email
            </p>
            <p className="text-ink">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Profile form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Title */}
        <div className="space-y-2">
          <label htmlFor="title" className="block text-sm font-medium text-ink">
            Professional title
          </label>
          <input
            id="title"
            name="title"
            type="text"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g., Full Stack Engineer"
            maxLength={200}
            aria-invalid={!!errors.title}
            className="input"
            style={errors.title ? { borderColor: 'var(--color-error)' } : undefined}
          />
          {errors.title && (
            <p role="alert" className="text-xs text-error font-medium">
              {errors.title}
            </p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-ink">
            Phone
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="e.g., +1 (555) 123-4567"
            maxLength={40}
            aria-invalid={!!errors.phone}
            className="input"
            style={errors.phone ? { borderColor: 'var(--color-error)' } : undefined}
          />
          {errors.phone && (
            <p role="alert" className="text-xs text-error font-medium">
              {errors.phone}
            </p>
          )}
        </div>

        {/* Location */}
        <div className="space-y-2">
          <label htmlFor="location" className="block text-sm font-medium text-ink">
            Location
          </label>
          <input
            id="location"
            name="location"
            type="text"
            value={formData.location}
            onChange={handleChange}
            placeholder="e.g., San Francisco, CA"
            maxLength={200}
            aria-invalid={!!errors.location}
            className="input"
            style={errors.location ? { borderColor: 'var(--color-error)' } : undefined}
          />
          {errors.location && (
            <p role="alert" className="text-xs text-error font-medium">
              {errors.location}
            </p>
          )}
        </div>

        {/* Bio */}
        <div className="space-y-2">
          <label htmlFor="bio" className="block text-sm font-medium text-ink">
            Bio
          </label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            placeholder="Tell us about yourself…"
            maxLength={5000}
            rows={6}
            aria-invalid={!!errors.bio}
            className="input resize-none"
            style={errors.bio ? { borderColor: 'var(--color-error)' } : undefined}
          />
          {errors.bio && (
            <p role="alert" className="text-xs text-error font-medium">
              {errors.bio}
            </p>
          )}
          <p className="text-xs text-ink-tertiary">
            {formData.bio.length}/5000
          </p>
        </div>

        <button type="submit" disabled={busy} className="btn-primary">
          {busy ? (
            <>
              <span
                className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'rgba(244, 240, 232, 0.35)',
                  borderTopColor: 'var(--color-cream)',
                }}
                aria-hidden="true"
              />
              <span>Saving…</span>
            </>
          ) : (
            'Save profile'
          )}
        </button>
      </form>
    </div>
  );
};

export default JobSeekerProfile;
